import React, { useState } from 'react';
import {
  Plus,
  Paintbrush,
  Loader2,
  Trash2,
  Rss,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { SocialCanvasEditor } from './SocialCanvasEditor';
import { FORMAT_PRESETS } from './formatPresets';
import { socialPostingService } from '../../../services/socialPosting';
import type { SocialPostDesign, SocialPostProduct, ProductSource } from '../../../services/socialPosting';
import type { BreadcrumbSegment } from '../../canvas-shared/CanvasBreadcrumb';

// ── Create Design Dialog ──────────────────────────────────────

function CreateDesignDialog({
  open,
  onClose,
  feedCount,
  csvCount,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  feedCount: number;
  csvCount: number;
  onCreate: (name: string, width: number, height: number, source: ProductSource) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(FORMAT_PRESETS[0].id);
  const [customWidth, setCustomWidth] = useState(1080);
  const [customHeight, setCustomHeight] = useState(1080);
  const [selectedSource, setSelectedSource] = useState<ProductSource>('feed');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    const isCustom = selectedPreset === 'custom';
    const preset = FORMAT_PRESETS.find((p) => p.id === selectedPreset);
    const width = isCustom ? customWidth : (preset?.width ?? 1080);
    const height = isCustom ? customHeight : (preset?.height ?? 1080);
    if (width < 100 || height < 100 || width > 4096 || height > 4096) return;
    const finalName = name.trim() || `Design ${Date.now().toString(36).slice(-4).toUpperCase()}`;
    try {
      setIsCreating(true);
      await onCreate(finalName, width, height, selectedSource);
      setName('');
      setSelectedPreset(FORMAT_PRESETS[0].id);
      onClose();
    } catch {
      // handled
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isCreating) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">New Design</DialogTitle>
          <DialogDescription className="text-sm">Choose a format and product source for your design.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-3">
          {/* Name */}
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Design name (optional)..."
            className="h-12 rounded-xl text-base"
            autoFocus
          />

          {/* Format */}
          <div className="space-y-3">
            <label className="text-base font-medium">Format</label>
            <div className="grid grid-cols-3 gap-3">
              {FORMAT_PRESETS.slice(0, 12).map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPreset(p.id)}
                  className={`
                    px-4 py-3 rounded-xl border-2 text-left transition-all
                    ${selectedPreset === p.id
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border hover:border-primary/40'
                    }
                  `}
                >
                  <div className="font-medium text-sm">{p.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.width} x {p.height}</div>
                </button>
              ))}
              <button
                onClick={() => setSelectedPreset('custom')}
                className={`
                  px-4 py-3 rounded-xl border-2 text-left transition-all
                  ${selectedPreset === 'custom'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:border-primary/40'
                  }
                `}
              >
                <div className="font-medium text-sm">Custom</div>
                <div className="text-xs text-muted-foreground mt-0.5">Enter size</div>
              </button>
            </div>
            {selectedPreset === 'custom' && (
              <div className="flex items-center gap-3 pt-1">
                <Input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Math.max(100, Math.min(4096, Number(e.target.value) || 100)))}
                  placeholder="Width"
                  className="h-11 rounded-xl text-sm"
                  min={100}
                  max={4096}
                />
                <span className="text-muted-foreground text-base shrink-0">x</span>
                <Input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Math.max(100, Math.min(4096, Number(e.target.value) || 100)))}
                  placeholder="Height"
                  className="h-11 rounded-xl text-sm"
                  min={100}
                  max={4096}
                />
                <span className="text-muted-foreground text-sm shrink-0">px</span>
              </div>
            )}
          </div>

          {/* Product Source */}
          <div className="space-y-3">
            <label className="text-base font-medium">Product Source</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSelectedSource('feed')}
                className={`
                  flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all
                  ${selectedSource === 'feed'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/40'
                  }
                `}
              >
                <Rss className={`h-6 w-6 shrink-0 ${selectedSource === 'feed' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <div className="text-base font-medium">Feed Products</div>
                  <div className="text-sm text-muted-foreground">{feedCount} product{feedCount !== 1 ? 's' : ''}</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedSource('csv')}
                className={`
                  flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all
                  ${selectedSource === 'csv'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/40'
                  }
                `}
              >
                <FileSpreadsheet className={`h-6 w-6 shrink-0 ${selectedSource === 'csv' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <div className="text-base font-medium">CSV Products</div>
                  <div className="text-sm text-muted-foreground">{csvCount} product{csvCount !== 1 ? 's' : ''}</div>
                </div>
              </button>
            </div>
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-400 leading-relaxed">
                Feed and CSV products use different field names. Choose the source that matches the products you want to use, so dynamic fields resolve correctly during export.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="rounded-lg h-10 px-5 text-sm"
              onClick={handleCreate}
              disabled={isCreating || (selectedPreset === 'custom' && (customWidth < 100 || customHeight < 100))}
            >
              {isCreating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create Design
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Canvas Manager ────────────────────────────────────────────

interface SocialCanvasManagerProps {
  postId: string;
  designs: SocialPostDesign[];
  isLoading: boolean;
  products: SocialPostProduct[];
  onDesignCreated: (design: SocialPostDesign) => void;
  onDesignUpdated: (design: SocialPostDesign) => void;
  onDesignDeleted: (designId: string) => void;
  breadcrumbSegments?: BreadcrumbSegment[];
}

export function SocialCanvasManager({
  postId,
  designs,
  isLoading,
  products,
  onDesignCreated,
  onDesignUpdated,
  onDesignDeleted,
  breadcrumbSegments,
}: SocialCanvasManagerProps) {
  const [activeDesign, setActiveDesign] = useState<SocialPostDesign | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const feedProducts = products.filter((p) => p.feed_id !== 'csv-import');
  const csvProducts = products.filter((p) => p.feed_id === 'csv-import');

  const handleCreate = async (name: string, width: number, height: number, source: ProductSource) => {
    const design = await socialPostingService.createDesign(postId, {
      name,
      width,
      height,
      elements: [],
      product_source: source,
    });
    onDesignCreated(design);
    setActiveDesign(design);
  };

  const handleDelete = async (e: React.MouseEvent, designId: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this design?')) return;
    try {
      setDeletingId(designId);
      await socialPostingService.deleteDesign(postId, designId);
      onDesignDeleted(designId);
    } catch {
      // handled
    } finally {
      setDeletingId(null);
    }
  };

  // Full-screen editor mode — filter products by design source
  if (activeDesign) {
    const sourceProducts = activeDesign.product_source === 'csv'
      ? products.filter((p) => p.feed_id === 'csv-import')
      : products.filter((p) => p.feed_id !== 'csv-import');

    return (
      <SocialCanvasEditor
        postId={postId}
        design={activeDesign}
        products={sourceProducts}
        onBack={() => setActiveDesign(null)}
        onDesignUpdated={(updated) => {
          setActiveDesign(updated);
          onDesignUpdated(updated);
        }}
        breadcrumbSegments={[
          ...(breadcrumbSegments ?? []),
          { label: 'Designs', onClick: () => setActiveDesign(null) },
        ]}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Designs</h3>
        <Button className="rounded-lg" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Design
        </Button>
      </div>

      {/* Design grid */}
      {designs.length === 0 ? (
        <div className="text-center py-24">
          <Paintbrush className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No designs yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Create a design to start building social posts.</p>
          <Button className="rounded-lg mt-4" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Design
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {designs.map((d) => (
            <Card
              key={d.id}
              className="border-2 rounded-xl overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              onClick={() => setActiveDesign(d)}
            >
              {/* Preview area */}
              <div className="aspect-video bg-muted/30 flex items-center justify-center relative">
                <div className="text-center">
                  <Paintbrush className="h-8 w-8 text-muted-foreground/40 mx-auto mb-1" />
                  <p className="text-xs text-muted-foreground">{d.width} x {d.height}</p>
                </div>
                <button
                  onClick={(e) => handleDelete(e, d.id)}
                  className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm border rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
                  disabled={deletingId === d.id}
                >
                  {deletingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium truncate flex-1">{d.name}</h4>
                  <Badge
                    variant="outline"
                    className={`text-[10px] shrink-0 ${
                      d.product_source === 'csv'
                        ? 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                        : 'bg-sky-500/15 text-sky-400 border-sky-500/30'
                    }`}
                  >
                    {d.product_source === 'csv' ? 'CSV' : 'Feed'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {d.elements.length} element{d.elements.length !== 1 ? 's' : ''} &middot; {new Date(d.updated_at).toLocaleDateString()}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Design Dialog */}
      <CreateDesignDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        feedCount={feedProducts.length}
        csvCount={csvProducts.length}
        onCreate={handleCreate}
      />
    </div>
  );
}
